import React, { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
    Box, Typography, Button, IconButton, Tooltip, Switch,
    LinearProgress, CircularProgress, Select, MenuItem,
    Dialog, DialogTitle, DialogContent, DialogActions, TextField
} from "@mui/material";
import {
    FaArrowUp, FaArrowDown, FaArrowLeft, FaArrowRight,
    FaHouse, FaMagnifyingGlassPlus, FaMagnifyingGlassMinus,
    FaCamera, FaRotate, FaStop, FaExpand, FaCircle,
    FaCalendarDays, FaChevronLeft, FaChevronRight, FaPlay,
    FaSliders, FaGear, FaCrosshairs, FaCheck, FaPlus,
    FaCompass, FaCompress, FaArrowUpRightFromSquare, FaDownload,
    FaCircleInfo
} from "react-icons/fa6";

import { PRESET, PRESETALL, LIVECAM } from "../../services/actions";
import "./ptz_station.css";

const resolveMediaUrl = (url) => {
    if (!url || typeof url !== "string") return "";
    const apiBase = (process.env.REACT_APP_URL || "http://127.0.0.1:8000/api/").replace(/\/api\/?$/, "");
    if (url.includes("localhost:8000") || url.includes("127.0.0.1:8000") || url.includes("localhost:3000")) {
        url = url.replace(/^http:\/\/(localhost|127\.0\.0\.1):(8000|3000)/, apiBase);
    }
    if (url.startsWith("http://") || url.startsWith("https://") || url.startsWith("data:") || url.startsWith("blob:")) {
        return url;
    }
    if (url.startsWith("/")) {
        return `${apiBase}${url}`;
    }
    return `${apiBase}/${url}`;
};

// Unique themes, distinct angles, and compass reticles for each preset
const PRESET_THEMES = {
    Home: { color: "#ffcc33", bg: "#1f1d10", angle: 0, label: "HOME VIEW" },
    "1": { color: "#38bdf8", bg: "#0c1d2e", angle: 45, label: "PRESET 1" },
    "2": { color: "#4ade80", bg: "#0d2818", angle: 120, label: "PRESET 2" },
    "3": { color: "#c084fc", bg: "#1f1033", angle: 210, label: "PRESET 3" },
    "4": { color: "#fb923c", bg: "#28170c", angle: 270, label: "PRESET 4" },
    "5": { color: "#2dd4bf", bg: "#0b2524", angle: 330, label: "PRESET 5" },
};

// Generates an inline crisp, distinct SVG thumbnail fallback for any preset (Zero bandwidth, distinct color/angle)
const getPresetFallbackSvg = (name) => {
    const cleanName = String(name || "PRESET").replace(/[<>&"]/g, "");
    const theme = PRESET_THEMES[cleanName] || {
        color: "#ffcc33",
        bg: "#14171d",
        angle: (Math.abs(cleanName.split("").reduce((a, c) => a + c.charCodeAt(0), 0)) * 53) % 360,
        label: "CALIBRATED ANGLE"
    };

    const rad = (theme.angle - 90) * (Math.PI / 180);
    const arrowX = 150 + 26 * Math.cos(rad);
    const arrowY = 65 + 26 * Math.sin(rad);

    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="300" height="170" viewBox="0 0 300 170"><defs><linearGradient id="g_${cleanName.replace(/[^a-zA-Z0-9]/g, '_')}" x1="0%" y1="0%" x2="100%" y2="100%"><stop offset="0%" stop-color="${theme.bg}"/><stop offset="100%" stop-color="#101214"/></linearGradient></defs><rect width="300" height="170" fill="url(#g_${cleanName.replace(/[^a-zA-Z0-9]/g, '_')})"/><rect x="6" y="6" width="288" height="158" rx="8" fill="none" stroke="${theme.color}" stroke-width="1.5" stroke-opacity="0.35"/><circle cx="150" cy="65" r="28" fill="#14171d" stroke="${theme.color}" stroke-width="2"/><circle cx="150" cy="65" r="6" fill="${theme.color}"/><line x1="150" y1="65" x2="${arrowX.toFixed(1)}" y2="${arrowY.toFixed(1)}" stroke="${theme.color}" stroke-width="2.5" stroke-linecap="round"/><text x="150" y="122" fill="#f1f5f9" font-family="Arial,sans-serif" font-size="12" font-weight="bold" text-anchor="middle" letter-spacing="0.6">${cleanName === "Home" ? "Home View" : "Preset " + cleanName}</text><text x="150" y="142" fill="${theme.color}" font-family="Arial,sans-serif" font-size="9" font-weight="bold" text-anchor="middle" letter-spacing="0.5">${theme.label} (${theme.angle}°)</text></svg>`;
    return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
};

const DEFAULT_PRESET_NAMES = [
    "Home",
    "1",
    "2",
    "3",
    "4",
    "5"
];

const DEFAULT_PRESETS = DEFAULT_PRESET_NAMES.map((name) => ({
    preset_name: name,
    image: getPresetFallbackSvg(name)
}));

const TIME_SLOTS = [
    { time: "06:00", label: "06:00 AM" },
    { time: "08:00", label: "08:00 AM" },
    { time: "10:00", label: "10:00 AM" },
    { time: "12:00", label: "12:00 PM" },
    { time: "14:00", label: "02:00 PM" },
    { time: "16:00", label: "04:00 PM" },
    { time: "18:00", label: "06:00 PM" },
];

// Rich Card Information Tooltip component (One info icon per card explaining everything inside)
const CardInfoTooltip = ({ title, purpose, details = [] }) => {
    const tooltipContent = (
        <Box sx={{ p: 1.2, maxWidth: 330 }}>
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#ffcc33", mb: 0.6, display: "flex", alignItems: "center", gap: 0.8 }}>
                <FaCircleInfo size={13} color="#ffcc33" /> {title}
            </Typography>
            {purpose && (
                <Typography sx={{ fontSize: 11, color: "#e2e8f0", mb: 0.8, lineHeight: 1.45 }}>
                    {purpose}
                </Typography>
            )}
            {details.length > 0 && (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 0.6, borderTop: "1px solid rgba(255,255,255,0.12)", pt: 0.8 }}>
                    {details.map((item, idx) => (
                        <Box key={idx} sx={{ fontSize: 10.5, lineHeight: 1.4, color: "#cbd5e1" }}>
                            <strong style={{ color: "#ffcc33" }}>{item.label}:</strong> {item.desc}
                        </Box>
                    ))}
                </Box>
            )}
        </Box>
    );

    return (
        <Tooltip
            title={tooltipContent}
            arrow
            placement="top"
            enterDelay={100}
            leaveDelay={200}
            componentsProps={{
                tooltip: {
                    sx: {
                        bgcolor: "rgba(18, 22, 30, 0.98)",
                        border: "1px solid rgba(255, 204, 51, 0.35)",
                        boxShadow: "0 8px 30px rgba(0,0,0,0.6)",
                        borderRadius: "8px",
                        p: 0,
                    }
                },
                arrow: {
                    sx: { color: "rgba(18, 22, 30, 0.98)" }
                }
            }}
        >
            <span className="card-info-trigger" role="button" tabIndex={0} style={{ cursor: "help", display: "inline-flex", alignItems: "center" }}>
                <FaCircleInfo size={13} />
            </span>
        </Tooltip>
    );
};

// Clean setting label without individual icons, keeping UI clean and uncluttered
const SettingLabel = ({ label }) => (
    <span className="ptz-settings-label">{label}</span>
);

export default function PtzStation() {
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch();

    const locationState = location.state || {};
    let initialCamera = locationState.camera;
    let initialCameras = locationState.cameras || [];
    let initialVideoMode = locationState.video_mode ?? true;

    if (!initialCamera) {
        try {
            const saved = JSON.parse(sessionStorage.getItem("last_selected_camera") || "null");
            if (saved?.camera) {
                initialCamera = saved.camera;
                initialCameras = saved.cameras || [];
                initialVideoMode = saved.video_mode ?? true;
            }
        } catch (e) {}
    } else {
        try {
            sessionStorage.setItem("last_selected_camera", JSON.stringify({
                camera: initialCamera,
                cameras: initialCameras,
                video_mode: initialVideoMode
            }));
        } catch (e) {}
    }

    const [camera, setCamera] = useState(initialCamera);
    const [cameras, setCameras] = useState(initialCameras);

    // PTZ Controls
    const [speed, setSpeed] = useState("medium"); // slow, medium, fast
    const [activePreset, setActivePreset] = useState("Home");
    const [isTouring, setIsTouring] = useState(false);
    const [ptzActionLoading, setPtzActionLoading] = useState(false);
    const [lastPtzStatus, setLastPtzStatus] = useState("");
    const [isMediaLoading, setIsMediaLoading] = useState(true);
    const [isPresetMediaLoading, setIsPresetMediaLoading] = useState(true);
    const [isWidelapseMediaLoading, setIsWidelapseMediaLoading] = useState(true);
    const [mediaStreamReady, setMediaStreamReady] = useState(false);

    // Tabs & views
    const [activeTopTab, setActiveTopTab] = useState("live");
    const [activeCaptureTab, setActiveCaptureTab] = useState("widelapse");
    const [selectedTimeSlot, setSelectedTimeSlot] = useState("10:00");
    const [selectedDate, setSelectedDate] = useState(() => {
        const d = new Date();
        return d.toISOString().split("T")[0];
    });

    // Schedule & info state (synced with camera model)
    const [enableWidelapse, setEnableWidelapse] = useState(() => {
        return Boolean(initialCamera?.network_timelapse ?? true);
    });
    const [scheduleInterval, setScheduleInterval] = useState(() => {
        return String(initialCamera?.image_upload_intervals || "15");
    });
    const [startTime, setStartTime] = useState("06:00");
    const [endTime, setEndTime] = useState("18:00");
    const [schedulePtzMode, setSchedulePtzMode] = useState("tour"); // 'current', 'tour', 'home'
    const [scheduleSaving, setScheduleSaving] = useState(false);

    // Camera Settings form state
    const [settingCamName, setSettingCamName] = useState(() => initialCamera?.camera_name || "Camera 01 - WIDELAPSE CAM");
    const [settingIpAddress, setSettingIpAddress] = useState(() => initialCamera?.ip_address || "192.168.10.25");
    const [settingResolution, setSettingResolution] = useState(() => initialCamera?.image_resolution || "3840 x 2160 (4K)");
    const [settingDwellTime, setSettingDwellTime] = useState(6);
    const [settingRetention, setSettingRetention] = useState(90);
    const [settingAutoHome, setSettingAutoHome] = useState(true);

    // Camera-specific persistent storage helpers for presets
    const getCameraPresetStorageKey = (camId) => `widelapse_presets_v2_${camId}`;

    const loadPersistedPresets = (camId) => {
        if (!camId) return {};
        try {
            const raw = localStorage.getItem(getCameraPresetStorageKey(camId));
            return raw ? JSON.parse(raw) : {};
        } catch (e) {
            console.warn("Failed to load persisted presets for camera", camId, e);
            return {};
        }
    };

    const persistPresetImage = (camId, presetName, imageData) => {
        if (!camId || !presetName || !imageData) return;
        try {
            const current = loadPersistedPresets(camId);
            current[presetName] = {
                image: imageData,
                updated_at: Date.now()
            };
            localStorage.setItem(getCameraPresetStorageKey(camId), JSON.stringify(current));
        } catch (e) {
            console.warn("Failed to persist preset image for camera", camId, e);
        }
    };

    // Presets from API & Local Persistence
    const [presetsList, setPresetsList] = useState(() => {
        if (!initialCamera?.id) return DEFAULT_PRESETS;
        const persisted = loadPersistedPresets(initialCamera.id);
        return DEFAULT_PRESETS.map((p) => {
            if (persisted[p.preset_name]?.image) {
                return { ...p, image: persisted[p.preset_name].image };
            }
            return p;
        });
    });
    const [isAddPresetOpen, setIsAddPresetOpen] = useState(false);
    const [newPresetName, setNewPresetName] = useState("");
    const [loadingPresetImage, setLoadingPresetImage] = useState(null);
    const [streamKey, setStreamKey] = useState(() => Date.now());
    const [selectedPresetView, setSelectedPresetView] = useState(null);

    useEffect(() => {
        setIsMediaLoading(true);
        const timer = setTimeout(() => {
            setIsMediaLoading(false);
        }, 1000);
        return () => clearTimeout(timer);
    }, [camera?.id, streamKey]);

    useEffect(() => {
        setIsPresetMediaLoading(true);
        const timer = setTimeout(() => {
            setIsPresetMediaLoading(false);
        }, 1000);
        return () => clearTimeout(timer);
    }, [camera?.id, streamKey]);

    useEffect(() => {
        setIsWidelapseMediaLoading(true);
        const timer = setTimeout(() => {
            setIsWidelapseMediaLoading(false);
        }, 1200);
        return () => clearTimeout(timer);
    }, [camera?.id, selectedDate, selectedTimeSlot]);

    // Live clock
    const [currentTimeStr, setCurrentTimeStr] = useState(() => {
        const d = new Date();
        return d.toISOString().replace("T", " ").slice(0, 19);
    });

    const videoContainerRef = useRef(null);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [snapshotToast, setSnapshotToast] = useState("");
    const tourTimerRef = useRef(null);
    const currentTourIndexRef = useRef(0);
    const presetCaptureTimerRef = useRef(null);

    useEffect(() => {
        const timer = setInterval(() => {
            const d = new Date();
            setCurrentTimeStr(d.toISOString().replace("T", " ").slice(0, 19));
        }, 1000);
        return () => clearInterval(timer);
    }, []);

    // Load presets on mount & hydrate camera-specific persistent store
    useEffect(() => {
        if (!camera?.id) return;
        const persisted = loadPersistedPresets(camera.id);

        // 1. Immediately hydrate from camera's persistent storage so UI never flickers to blank on refresh
        setPresetsList((prev) => {
            const base = prev && prev.length > 0 ? prev : DEFAULT_PRESETS;
            const updated = base.map((p) => {
                if (persisted[p.preset_name]?.image) {
                    return { ...p, image: persisted[p.preset_name].image };
                }
                return p;
            });
            // Include custom saved presets for this camera
            Object.keys(persisted).forEach((pName) => {
                if (["WIDELAPSE_HOME", "ZONE_A", "ZONE_B", "ZONE_C", "GATE", "CRANE"].includes(pName)) return;
                if (!updated.some((p) => p.preset_name === pName)) {
                    updated.push({
                        preset_name: pName,
                        image: persisted[pName].image || getPresetFallbackSvg(pName)
                    });
                }
            });
            return updated;
        });

        // 2. Fetch from backend and merge seamlessly
        dispatch({
            type: `${PRESETALL}_GET`,
            payload: camera.id,
            callback: (resp) => {
                if (resp?.results && Array.isArray(resp.results) && resp.results.length > 0) {
                    const latestPersisted = loadPersistedPresets(camera.id);
                    const formatted = resp.results.map((p) => {
                        const pName = String(p.preset_name);
                        const hasValidServerImage =
                            p.image &&
                            typeof p.image === "string" &&
                            p.image.trim() !== "" &&
                            !p.image.includes("None");

                        let finalImage = hasValidServerImage ? p.image : null;
                        if (!finalImage && latestPersisted[pName]?.image) {
                            finalImage = latestPersisted[pName].image;
                        }
                        if (!finalImage) {
                            finalImage = getPresetFallbackSvg(pName);
                        } else if (hasValidServerImage) {
                            persistPresetImage(camera.id, pName, p.image);
                        }

                        return {
                            ...p,
                            preset_name: pName,
                            image: finalImage
                        };
                    });

                    // Sort: Home first, then numerical presets 1, 2, 3...
                    formatted.sort((a, b) => {
                        if (a.preset_name.toLowerCase() === "home") return -1;
                        if (b.preset_name.toLowerCase() === "home") return 1;
                        return a.preset_name.localeCompare(b.preset_name, undefined, { numeric: true });
                    });

                    setPresetsList(formatted);
                    if (formatted.length > 0 && !formatted.some((p) => p.preset_name === activePreset)) {
                        setActivePreset(formatted[0].preset_name);
                    }
                }
            }
        });
    }, [camera?.id]);

    // Clean up tour timer and preset capture timer on unmount
    useEffect(() => {
        return () => {
            if (tourTimerRef.current) clearInterval(tourTimerRef.current);
            if (presetCaptureTimerRef.current) clearTimeout(presetCaptureTimerRef.current);
        };
    }, []);

    const handlePtz = (direction, customParams = {}, onDone = null) => {
        if (!camera?.id) return;
        if (["up", "down", "left", "right", "zoom-in", "zoom-out", "in", "out", "home", "reset", "center"].includes(direction)) {
            setSelectedPresetView(null);
        }
        setPtzActionLoading(true);
        const autoClear = setTimeout(() => {
            setPtzActionLoading(false);
        }, 2200);

        dispatch({
            type: `${PRESET}_POST`,
            payload: {
                id: camera.id,
                direction,
                speed,
                ...customParams,
            },
            callback: (res) => {
                clearTimeout(autoClear);
                setPtzActionLoading(false);
                if (onDone) onDone(res);
            }
        });
    };

    // Helper: Safely grab the current crisp image from the live video stream element
    const getCurrentLiveFrameDataUrl = () => {
        const container = videoContainerRef.current;
        const video = container?.querySelector("video");
        const img = container?.querySelector("img.ptz-stream-media") || container?.querySelector("img");
        const el = (mediaStreamReady && video) ? video : (img || video);
        if (el) {
            try {
                const width = el.videoWidth || el.naturalWidth || 1920;
                const height = el.videoHeight || el.naturalHeight || 1080;
                const canvas = document.createElement("canvas");
                canvas.width = width;
                canvas.height = height;
                const ctx = canvas.getContext("2d");
                ctx.drawImage(el, 0, 0, width, height);
                return canvas.toDataURL("image/jpeg", 0.9);
            } catch (e) {
                console.warn("Canvas capture tainted or restricted:", e);
            }
        }
        return null;
    };

    // Helper: Safely get static thumbnail for a preset with distinct thematic fallback
    const getPresetThumbnail = (item) => {
        if (
            item?.image &&
            typeof item.image === "string" &&
            item.image.trim() !== "" &&
            !item.image.includes("None")
        ) {
            return item.image;
        }
        return getPresetFallbackSvg(item?.preset_name || "PRESET");
    };

    // Helper: Detect if a preset image is blank or placeholder
    const isPresetImageBlank = (preset) => {
        if (!preset) return true;
        const img = typeof preset === "string" ? preset : preset?.image;
        if (!img || typeof img !== "string") return true;
        const trimmed = img.trim();
        if (!trimmed || trimmed === "None" || trimmed === "null" || trimmed === "undefined") return true;
        if (trimmed.startsWith("data:image/svg") || trimmed.includes("<svg") || trimmed.includes("xmlns=\"http://www.w3.org/2000/svg\"")) return true;
        return false;
    };

    // Instant snapshot capture and permanent persistence for preset card
    const capturePresetImageWithoutFlicker = (presetName, isAutoCapture = false) => {
        if (!presetName) return;
        setLoadingPresetImage(presetName);

        // 1. Try instantaneous canvas extraction from active live stream element
        const canvasDataUrl = getCurrentLiveFrameDataUrl();

        if (canvasDataUrl) {
            setPresetsList((prev) =>
                prev.map((p) =>
                    p.preset_name === presetName ? { ...p, image: canvasDataUrl } : p
                )
            );
            if (camera?.id) {
                persistPresetImage(camera.id, presetName, canvasDataUrl);
            }
        }

        // 2. Sync with backend for permanent file & DB storage (without touching hardware preset position when auto-capturing)
        if (camera?.id) {
            dispatch({
                type: `${PRESET}_PATCH`,
                payload: {
                    id: camera.id,
                    preset: presetName,
                    image: canvasDataUrl || "",
                    update_position: !isAutoCapture, // NEVER alter hardware preset position when auto-capturing!
                    image_only: isAutoCapture
                },
                callback: (res) => {
                    const finalImg = res?.data?.image || res?.image || canvasDataUrl;
                    if (finalImg) {
                        setPresetsList((prev) =>
                            prev.map((p) =>
                                p.preset_name === presetName ? { ...p, image: finalImg } : p
                            )
                        );
                        persistPresetImage(camera.id, presetName, finalImg);
                    }
                    setLoadingPresetImage(null);
                }
            });
        } else {
            setLoadingPresetImage(null);
        }
    };

    // Update Preset: Overwrites camera position and saves image permanently ONLY AFTER API call completes
    const handleUpdatePreset = (presetName) => {
        if (!camera?.id || !presetName) return;
        setLoadingPresetImage(presetName);

        // 1. Command PTZ motor to register current position and wait for API call to complete
        handlePtz("set_preset", { preset_name: presetName, preset: "set" }, () => {
            // 2. Only after API call completes successfully, capture live frame
            const capturedDataUrl = getCurrentLiveFrameDataUrl();
            if (capturedDataUrl) {
                setPresetsList((prev) =>
                    prev.map((p) =>
                        p.preset_name === presetName ? { ...p, image: capturedDataUrl } : p
                    )
                );
                persistPresetImage(camera.id, presetName, capturedDataUrl);
            }

            // 3. Dispatch to backend for permanent filesystem & DB storage
            dispatch({
                type: `${PRESET}_PATCH`,
                payload: {
                    id: camera.id,
                    preset: presetName,
                    image: capturedDataUrl || "",
                    update_position: false // Already registered via set_preset API call above
                },
                callback: (res) => {
                    const finalImg = res?.data?.image || res?.image || capturedDataUrl;
                    if (finalImg) {
                        setPresetsList((prev) =>
                            prev.map((p) =>
                                p.preset_name === presetName ? { ...p, image: finalImg } : p
                            )
                        );
                        persistPresetImage(camera.id, presetName, finalImg);
                    }
                    setLoadingPresetImage(null);
                    setStreamKey(Date.now());
                }
            });
        });
    };

    // Move to preset and update thumbnail ONLY after PTZ API call returns and camera completes rotation
    const handleGotoPreset = (presetName, isAutoTour = false) => {
        const cleanName = String(presetName);
        setActivePreset(cleanName);

        // Cancel any pending capture timer from previous clicks so we never capture the wrong preset
        if (presetCaptureTimerRef.current) {
            clearTimeout(presetCaptureTimerRef.current);
            presetCaptureTimerRef.current = null;
        }

        if (isAutoTour) {
            handlePtz("goto_preset", { preset_name: cleanName });
            return;
        }

        setLoadingPresetImage(cleanName);

        // 1. Dispatch PTZ goto_preset API call to camera
        handlePtz("goto_preset", { preset_name: cleanName }, (res) => {
            // 2. ONLY after the PTZ API call completes loading:
            // Allow time for the physical camera motors to complete rotation/pan/tilt/zoom and live video frame to settle
            presetCaptureTimerRef.current = setTimeout(() => {
                // 3. Capture and save the fresh arrived image so preset thumbnail perfectly matches the real view!
                capturePresetImageWithoutFlicker(cleanName, true);
                presetCaptureTimerRef.current = null;
            }, 3500);
        });
    };

    // Save Preset: ONLY called when user explicitly submits the modal
    const handleSaveNewPreset = () => {
        if (!newPresetName.trim() || !camera?.id) return;
        const pName = newPresetName.trim();
        const capturedDataUrl = getCurrentLiveFrameDataUrl();

        // Immediate optimistic UI update
        if (capturedDataUrl) {
            persistPresetImage(camera.id, pName, capturedDataUrl);
            setPresetsList((prev) => [
                ...prev.filter((p) => p.preset_name !== pName),
                { preset_name: pName, image: capturedDataUrl }
            ]);
        }
        setIsAddPresetOpen(false);
        setActivePreset(pName);
        setSelectedPresetView(pName);
        setNewPresetName("");

        dispatch({
            type: `${PRESET}_PATCH`,
            payload: {
                id: camera.id,
                preset: pName,
                image: capturedDataUrl || ""
            },
            callback: (res) => {
                const finalImg = res?.data?.image || res?.image || capturedDataUrl || getPresetFallbackSvg(pName);
                if (finalImg) {
                    persistPresetImage(camera.id, pName, finalImg);
                    setPresetsList((prev) => [
                        ...prev.filter((p) => p.preset_name !== pName),
                        { preset_name: pName, image: finalImg }
                    ]);
                }
                setStreamKey(Date.now());
            }
        });
    };

    // Tour functionality: Smooth, non-flickering patrol across presets
    const handleToggleTour = () => {
        if (isTouring) {
            // STOP TOUR
            if (tourTimerRef.current) {
                clearInterval(tourTimerRef.current);
                tourTimerRef.current = null;
            }
            setIsTouring(false);
            handlePtz("stop");
            setSnapshotToast("Tour stopped.");
            setTimeout(() => setSnapshotToast(""), 2000);
        } else {
            // START TOUR
            setIsTouring(true);
            setSnapshotToast("Tour started! Patrolling presets...");
            setTimeout(() => setSnapshotToast(""), 3000);

            // Trigger backend hardware patrol
            handlePtz("tour", { tour: "start" });

            const list = presetsList.length > 0 ? presetsList : DEFAULT_PRESETS;
            currentTourIndexRef.current = 0;

            // Visit first preset
            handleGotoPreset(list[0].preset_name, true);

            // Cycle presets every 6.5 seconds
            tourTimerRef.current = setInterval(() => {
                currentTourIndexRef.current = (currentTourIndexRef.current + 1) % list.length;
                const nextPreset = list[currentTourIndexRef.current];
                if (nextPreset) {
                    handleGotoPreset(nextPreset.preset_name, true);
                }
            }, 6500);
        }
    };

    // Real Snapshot Browser Download (Direct file download, NEVER opens in new tab or URL)
    const handleDownloadSnapshot = async (targetUrl = null, customFilename = null) => {
        setSnapshotToast("Capturing snapshot...");
        const d = new Date();
        const timestamp = d.toISOString().replace(/[:.]/g, "-").slice(0, 19);
        const fileName = customFilename || `${camera?.camera_name || "Camera"}_${timestamp}.jpg`;

        // Direct Blob Download Helper
        const triggerBlobDownload = (blob) => {
            const blobUrl = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.style.display = "none";
            link.href = blobUrl;
            link.download = fileName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            setTimeout(() => window.URL.revokeObjectURL(blobUrl), 2000);
            setSnapshotToast(`Snapshot downloaded: ${fileName}`);
            setTimeout(() => setSnapshotToast(""), 3500);
        };

        // 1. Try Canvas Capture from currently visible image/video in the player
        const container = videoContainerRef.current;
        const video = container?.querySelector("video");
        const img = container?.querySelector("img");
        const el = (mediaStreamReady && video) ? video : (img || video);
        if (el) {
            try {
                const width = el.videoWidth || el.naturalWidth || 1920;
                const height = el.videoHeight || el.naturalHeight || 1080;
                const canvas = document.createElement("canvas");
                canvas.width = width;
                canvas.height = height;
                const ctx = canvas.getContext("2d");
                ctx.drawImage(el, 0, 0, width, height);
                canvas.toBlob((blob) => {
                    if (blob && blob.size > 0) {
                        triggerBlobDownload(blob);
                        return;
                    }
                    downloadThroughBlobFetch();
                }, "image/jpeg", 0.95);
                return;
            } catch (err) {
                console.warn("Canvas capture tainted or cross-origin, fetching image as blob...", err);
            }
        }

        downloadThroughBlobFetch();

        async function downloadThroughBlobFetch() {
            const candidates = [
                targetUrl,
                camera?.id ? `/api/image/jpeg/?id=${camera.id}` : null,
                camera?.thumbnail,
                camera?.image_url,
                mjpegSrc,
                mediaSrc
            ].filter(Boolean);

            for (const url of candidates) {
                try {
                    const res = await fetch(url, { mode: "cors", credentials: "omit" });
                    if (res.ok) {
                        const blob = await res.blob();
                        if (blob && blob.size > 0) {
                            triggerBlobDownload(blob);
                            return;
                        }
                    }
                } catch (e) {
                    // continue to next candidate
                }
            }

            // Fallback: Use XMLHttpRequest with blob responseType
            const fallbackUrl = candidates[0];
            if (fallbackUrl) {
                try {
                    const xhr = new XMLHttpRequest();
                    xhr.open("GET", fallbackUrl, true);
                    xhr.responseType = "blob";
                    xhr.onload = function () {
                        if (this.status === 200 && this.response) {
                            triggerBlobDownload(this.response);
                        } else {
                            const link = document.createElement("a");
                            link.style.display = "none";
                            link.href = fallbackUrl;
                            link.download = fileName;
                            document.body.appendChild(link);
                            link.click();
                            document.body.removeChild(link);
                            setSnapshotToast(`Snapshot downloaded: ${fileName}`);
                            setTimeout(() => setSnapshotToast(""), 3000);
                        }
                    };
                    xhr.onerror = function () {
                        const link = document.createElement("a");
                        link.style.display = "none";
                        link.href = fallbackUrl;
                        link.download = fileName;
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);
                        setSnapshotToast(`Snapshot downloaded: ${fileName}`);
                        setTimeout(() => setSnapshotToast(""), 3000);
                    };
                    xhr.send();
                    return;
                } catch (err) {}
            }

            setSnapshotToast("Failed to download snapshot.");
            setTimeout(() => setSnapshotToast(""), 3000);
        }
    };

    // Save Schedule changes to backend Camera model
    const handleSaveSchedule = (newEnabled, newInterval, newPtzMode) => {
        if (!camera?.id) return;
        setScheduleSaving(true);
        dispatch({
            type: `${LIVECAM}_PATCH`,
            payload: {
                id: camera.id,
                network_timelapse: newEnabled,
                image_upload_intervals: parseInt(newInterval, 10) || 15,
            },
            callback: (res) => {
                setScheduleSaving(false);
                setSnapshotToast("Schedule settings saved to camera!");
                setTimeout(() => setSnapshotToast(""), 2500);
                if (res?.data) {
                    setCamera(res.data);
                }
            }
        });
    };

    // Save All Camera Settings to backend
    const handleSaveAllSettings = () => {
        if (!camera?.id) {
            setSnapshotToast("Settings updated locally!");
            setTimeout(() => setSnapshotToast(""), 2500);
            return;
        }
        setScheduleSaving(true);
        dispatch({
            type: `${LIVECAM}_PATCH`,
            payload: {
                id: camera.id,
                camera_name: settingCamName,
                ip_address: settingIpAddress,
                network_timelapse: enableWidelapse,
                image_upload_intervals: parseInt(scheduleInterval, 10) || 15,
            },
            callback: (res) => {
                setScheduleSaving(false);
                setSnapshotToast("All camera settings saved successfully!");
                setTimeout(() => setSnapshotToast(""), 3000);
                if (res?.data) {
                    setCamera(res.data);
                }
            }
        });
    };

    const toggleFullscreen = () => {
        if (!document.fullscreenElement) {
            videoContainerRef.current?.requestFullscreen?.();
            setIsFullscreen(true);
        } else {
            document.exitFullscreen?.();
            setIsFullscreen(false);
        }
    };

    const navigateToFullViewer = () => {
        navigate("/camera-preview/", {
            state: {
                camera,
                cameras,
                video_mode: true
            }
        });
    };

    // 1. Fast Initial JPEG / MJPEG Stream (loads immediately with 0 delay)
    const mjpegSrc = useMemo(() => {
        let candidate =
            camera?.video_url ||
            (camera?.id ? `/api/video/stream/?id=${camera.id}&` : "");
        if (!candidate && camera?.stream_url && !camera.stream_url.includes("stream2")) {
            candidate = camera.stream_url;
        }
        return resolveMediaUrl(candidate);
    }, [camera?.id, camera?.video_url, camera?.stream_url]);

    // 2. Continuous Media Streaming (H.264 / MP4 video encoder - loads in background)
    const mediaSrc = useMemo(() => {
        let candidate =
            camera?.stream_url ||
            (camera?.id ? `/api/video/stream2/?id=${camera.id}&action=stream` : "");
        if (!candidate || candidate === mjpegSrc) {
            return null;
        }
        return resolveMediaUrl(candidate);
    }, [camera?.id, camera?.stream_url, mjpegSrc]);

    // Active Preset Object & Corresponding Image
    const activePresetObj = useMemo(() => {
        return presetsList.find((p) => p.preset_name === activePreset) || null;
    }, [presetsList, activePreset]);

    const activePresetImage = useMemo(() => {
        if (activePresetObj?.image && typeof activePresetObj.image === "string" && activePresetObj.image.trim() !== "") {
            return activePresetObj.image;
        }
        return "";
    }, [activePresetObj]);

    return (
        <Box className="ptz-station-root">
            {/* Top Navigation & Status Bar */}
            <Box className="ptz-station-header">
                <Box className="ptz-station-header-left">
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Button
                            size="small"
                            variant="outlined"
                            onClick={() => navigate("/cameras/")}
                            sx={{
                                color: "#ffcc33",
                                borderColor: "rgba(255, 204, 51, 0.4)",
                                textTransform: "none",
                                fontSize: "11px",
                                py: 0.3,
                                px: 1,
                                mr: 0.5,
                                "&:hover": { borderColor: "#ffcc33", background: "rgba(255,204,51,0.1)" }
                            }}
                        >
                            ← Cameras
                        </Button>
                        <Box className="ptz-station-title-box">
                            <span className="ptz-station-status-dot"></span>
                            <Typography className="ptz-station-title" component="span">
                                {camera?.camera_name || "Camera 01 - WIDELAPSE CAM"}
                            </Typography>
                            <span className="ptz-station-online-badge">Online</span>
                        </Box>
                    </Box>

                    <Typography className="ptz-station-metadata">
                        <span><strong>Project:</strong> {camera?.project_name || "Metro Construction"}</span>
                        <span className="sep">|</span>
                        <span><strong>Location:</strong> {camera?.camera_location || "Site Perimeter"}</span>
                        <span className="sep">|</span>
                        <span><strong>Resolution:</strong> {camera?.image_resolution || "3840 x 2160 (4K)"}</span>
                        <span className="sep">|</span>
                        <span><strong>25 fps</strong></span>
                    </Typography>
                </Box>

                <Box className="ptz-station-header-right">
                    <Box className="ptz-station-nav-tabs">
                        <Tooltip title="Real-time interactive camera video stream with manual pan, tilt, zoom and snapshot download" arrow placement="bottom">
                            <button
                                className={`ptz-nav-pill ${activeTopTab === "live" ? "active" : ""}`}
                                onClick={() => setActiveTopTab("live")}
                            >
                                Live View
                            </button>
                        </Tooltip>
                        <Tooltip title="Manage and calibrate camera preset angles, save new points of interest, and run patrol tours" arrow placement="bottom">
                            <button
                                className={`ptz-nav-pill ${activeTopTab === "preset" ? "active" : ""}`}
                                onClick={() => setActiveTopTab("preset")}
                            >
                                Preset View
                            </button>
                        </Tooltip>
                        <Tooltip title="High-resolution wide timelapse photo gallery, daily time slots, and instant capture triggers" arrow placement="bottom">
                            <button
                                className={`ptz-nav-pill ${activeTopTab === "widelapse" ? "active" : ""}`}
                                onClick={() => setActiveTopTab("widelapse")}
                            >
                                Widelapse
                            </button>
                        </Tooltip>
                        <Tooltip title="Camera configuration, automated schedules, PTZ speeds, and cloud retention rules" arrow placement="bottom">
                            <button
                                className={`ptz-nav-pill ${activeTopTab === "settings" ? "active" : ""}`}
                                onClick={() => setActiveTopTab("settings")}
                            >
                                Settings
                            </button>
                        </Tooltip>
                    </Box>

                    <Tooltip title="Open Full Drag-to-Zoom Interactive Viewer">
                        <Button
                            variant="contained"
                            size="small"
                            onClick={navigateToFullViewer}
                            startIcon={<FaArrowUpRightFromSquare size={11} />}
                            sx={{
                                background: "#ffcc33",
                                color: "#000",
                                fontWeight: 700,
                                fontSize: "11px",
                                textTransform: "none",
                                borderRadius: "6px",
                                boxShadow: "0 2px 8px rgba(255,204,51,0.35)",
                                "&:hover": { background: "#ffd65c" }
                            }}
                        >
                            Interactive Viewer
                        </Button>
                    </Tooltip>
                </Box>
            </Box>

            {/* Main Content Layout: Left Stream/Timeline & Right PTZ Sidebar */}
            {activeTopTab === "live" && (
            <Box className="ptz-station-body">
                {/* Left Column: Video, Timeline, and Info Cards */}
                <Box className="ptz-station-main-col">
                    {/* Live Stream Container */}
                    <Box className="ptz-video-container" ref={videoContainerRef}>
                        {/* 1. Fast Initial JPEG / MJPEG Stream - Displays immediately with 0 delay */}
                        {mjpegSrc && (
                            <img
                                key={`mjpeg_${streamKey}`}
                                src={`${mjpegSrc}${mjpegSrc.includes("?") ? "&" : "?"}_t=${streamKey}`}
                                alt="Live JPEG Stream"
                                crossOrigin="anonymous"
                                className="ptz-stream-media"
                                onLoad={() => setIsMediaLoading(false)}
                                onError={() => {
                                    setIsMediaLoading(false);
                                }}
                                ref={(el) => {
                                    if (el && el.complete && el.naturalWidth > 0 && isMediaLoading) {
                                        setIsMediaLoading(false);
                                    }
                                }}
                                style={{
                                    position: "absolute",
                                    top: 0,
                                    left: 0,
                                    width: "100%",
                                    height: "100%",
                                    objectFit: "contain",
                                    opacity: mediaStreamReady ? 0 : 1,
                                    transition: "opacity 0.4s ease-in-out",
                                    pointerEvents: mediaStreamReady ? "none" : "auto",
                                    zIndex: mediaStreamReady ? 1 : 2,
                                }}
                            />
                        )}

                        {/* 2. Continuous Media Streaming (H.264 / MP4 video encoder) - Loads in background and takes over */}
                        {mediaSrc && (
                            <video
                                key={`media_${streamKey}`}
                                src={`${mediaSrc}${mediaSrc.includes("?") ? "&" : "?"}_t=${streamKey}`}
                                crossOrigin="anonymous"
                                muted
                                playsInline
                                autoPlay
                                className="ptz-stream-media"
                                onCanPlay={() => {
                                    setMediaStreamReady(true);
                                    setIsMediaLoading(false);
                                }}
                                onPlaying={() => {
                                    setMediaStreamReady(true);
                                    setIsMediaLoading(false);
                                }}
                                onError={() => {
                                    setMediaStreamReady(false);
                                }}
                                style={{
                                    position: "absolute",
                                    top: 0,
                                    left: 0,
                                    width: "100%",
                                    height: "100%",
                                    objectFit: "contain",
                                    opacity: mediaStreamReady ? 1 : 0,
                                    transition: "opacity 0.4s ease-in-out",
                                    pointerEvents: mediaStreamReady ? "auto" : "none",
                                    zIndex: mediaStreamReady ? 2 : 1,
                                }}
                            />
                        )}

                        {!mjpegSrc && !mediaSrc && (
                            <Box className="ptz-stream-placeholder">
                                <CircularProgress size={40} sx={{ color: "#ffcc33" }} />
                                <Typography sx={{ mt: 1.5, color: "#aaa", fontSize: 13 }}>
                                    Connecting to camera stream...
                                </Typography>
                            </Box>
                        )}

                        {isMediaLoading && (
                            <Box className="ptz-centered-media-loader">
                                <CircularProgress size={44} sx={{ color: "#ffcc33", mb: 1.5 }} thickness={4} />
                                <Typography sx={{ color: "#ffcc33", fontSize: "13px", fontWeight: 700, letterSpacing: "0.5px" }}>
                                    {selectedPresetView ? `Loading Preset: ${selectedPresetView}...` : "Loading Camera Stream..."}
                                </Typography>
                            </Box>
                        )}

                        {/* Top Left HUD */}
                        <Box className="ptz-hud-top-left">
                            <Typography className="hud-text-time">{currentTimeStr}</Typography>
                            <Typography className="hud-text-model">
                                {selectedPresetView ? `Preset View: ${selectedPresetView}` : "WIDELAPSE CAM"} &nbsp;|&nbsp; 4K Live
                            </Typography>
                        </Box>

                        {/* Top Right HUD Action Buttons */}
                        <Box className="ptz-hud-top-right">
                            <Tooltip title="Fullscreen">
                                <button className="hud-action-btn" onClick={toggleFullscreen}>
                                    {isFullscreen ? <FaCompress size={13} /> : <FaExpand size={13} />}
                                </button>
                            </Tooltip>
                            <Tooltip title="Capture & Download Snapshot">
                                <button className="hud-action-btn" onClick={handleDownloadSnapshot}>
                                    <FaCamera size={13} />
                                </button>
                            </Tooltip>
                        </Box>

                        {/* Bottom Left HUD: Preset Indicator */}
                        <Box className="ptz-hud-bottom-left">
                            <span className="preset-hud-tag">
                                Preset: <strong>{activePreset === "Home" ? "Home View" : "Preset " + activePreset}</strong>
                            </span>
                            {isTouring && (
                                <span className="ptz-hud-tour-badge">
                                    <FaRotate size={10} className="spinning" style={{ marginRight: 5 }} />
                                    Tour Active
                                </span>
                            )}
                        </Box>

                        {/* Bottom Right HUD: Direction Reticle */}
                        <Box className="ptz-hud-bottom-right">
                            <Tooltip title="Open Drag-to-Zoom Full Viewer">
                                <button className="hud-reticle-btn" onClick={navigateToFullViewer}>
                                    <FaCompass size={18} />
                                </button>
                            </Tooltip>
                        </Box>

                        {/* Snapshot Toast notification */}
                        {snapshotToast && (
                            <Box className="ptz-snapshot-toast">
                                <FaCheck size={12} style={{ marginRight: 6 }} />
                                {snapshotToast}
                            </Box>
                        )}
                    </Box>

                    {/* Timeline & Captures Bar */}
                    <Box className="ptz-timeline-card">
                        <Box className="ptz-timeline-header">
                            <Box className="ptz-timeline-subtabs">
                                <button
                                    className={`subtab-btn ${activeCaptureTab === "widelapse" ? "active" : ""}`}
                                    onClick={() => setActiveCaptureTab("widelapse")}
                                >
                                    Widelapse Images
                                </button>
                                <button
                                    className={`subtab-btn ${activeCaptureTab === "recent" ? "active" : ""}`}
                                    onClick={() => setActiveCaptureTab("recent")}
                                >
                                    Recent Captures
                                </button>
                                <button
                                    className={`subtab-btn ${activeCaptureTab === "video" ? "active" : ""}`}
                                    onClick={() => setActiveCaptureTab("video")}
                                >
                                    Timelapse Video
                                </button>
                            </Box>

                            <Box className="ptz-timeline-date-selector">
                                <FaCalendarDays size={13} className="cal-icon" />
                                <input
                                    type="date"
                                    className="ptz-date-input"
                                    value={selectedDate}
                                    onChange={(e) => setSelectedDate(e.target.value)}
                                />
                                <button
                                    className="date-nav-btn"
                                    onClick={() => {
                                        const d = new Date(selectedDate);
                                        d.setDate(d.getDate() - 1);
                                        setSelectedDate(d.toISOString().split("T")[0]);
                                    }}
                                >
                                    <FaChevronLeft size={10} />
                                </button>
                                <button
                                    className="date-nav-btn"
                                    onClick={() => {
                                        const d = new Date(selectedDate);
                                        d.setDate(d.getDate() + 1);
                                        setSelectedDate(d.toISOString().split("T")[0]);
                                    }}
                                >
                                    <FaChevronRight size={10} />
                                </button>
                            </Box>
                        </Box>

                        {/* Thumbnails Carousel - STATIC THUMBNAILS (No stream fallback to prevent connection limits & flickering) */}
                        <Box className="ptz-timeline-strip-wrapper">
                            <button className="strip-arrow-btn left">
                                <FaChevronLeft size={12} />
                            </button>
                            <Box className="ptz-timeline-strip">
                                {TIME_SLOTS.map((slot) => {
                                    const isSelected = selectedTimeSlot === slot.time;
                                    return (
                                        <Box
                                            key={slot.time}
                                            className={`timeline-slot-item ${isSelected ? "selected" : ""}`}
                                            onClick={() => setSelectedTimeSlot(slot.time)}
                                        >
                                            <Box className="slot-thumb-container">
                                                <img
                                                    src={camera?.thumbnail || camera?.image_url || getPresetFallbackSvg(slot.time)}
                                                    alt={slot.time}
                                                    className="slot-thumb-img"
                                                    onError={(e) => {
                                                        e.target.onerror = null;
                                                        e.target.src = getPresetFallbackSvg(slot.time);
                                                    }}
                                                />
                                                {isSelected && <span className="slot-active-dot"></span>}
                                            </Box>
                                            <span className="slot-time-text">{slot.time}</span>
                                        </Box>
                                    );
                                })}
                            </Box>
                            <button className="strip-arrow-btn right">
                                <FaChevronRight size={12} />
                            </button>
                        </Box>
                    </Box>

                    {/* Bottom Status / Info 3 Cards */}
                    <Box className="ptz-info-cards-row">
                        {/* Card 1: Camera Information */}
                        <Box className="ptz-info-card">
                            <Typography className="card-heading" sx={{ display: "inline-flex", alignItems: "center", gap: 0.8 }}>
                                Camera Information
                                <CardInfoTooltip
                                    title="Camera Specifications & Status"
                                    purpose="Displays detected hardware attributes, operating network parameters, and live system uptime for this camera station."
                                    details={[
                                        { label: "Model", desc: "Hardware series, optical capabilities, and camera specifications." },
                                        { label: "Firmware", desc: "Installed camera firmware OS version." },
                                        { label: "Uptime", desc: "Continuous uptime since last power cycle or reboot." }
                                    ]}
                                />
                            </Typography>
                            <Box className="card-data-table">
                                <Box className="data-row">
                                    <span className="data-label">Model</span>
                                    <span className="data-value">WIDELAPSE CAM</span>
                                </Box>
                                <Box className="data-row">
                                    <span className="data-label">Firmware</span>
                                    <span className="data-value">WIDELAPSE OS 2.4.1</span>
                                </Box>
                                <Box className="data-row">
                                    <span className="data-label">Uptime</span>
                                    <span className="data-value">5 days 03:12:45</span>
                                </Box>
                            </Box>
                        </Box>

                        {/* Card 2: Widelapse Schedule & PTZ Actions */}
                        <Box className="ptz-info-card">
                            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.5 }}>
                                <Typography className="card-heading" sx={{ mb: "0 !important", display: "inline-flex", alignItems: "center", gap: 0.8 }}>
                                    Widelapse Schedule
                                    <CardInfoTooltip
                                        title="Widelapse Automated Capture Guide"
                                        purpose="Configures automated periodic timelapse snapshot capture and cloud synchronization during site operating hours."
                                        details={[
                                            { label: "Toggle Switch", desc: "Enables or disables automatic periodic timelapse capture." },
                                            { label: "Interval", desc: "How often photos are taken (10, 15, 30, or 60 minutes)." },
                                            { label: "Time Window", desc: "Active daily hours (e.g. 06:00 AM to 06:00 PM). Automatically pauses at night to avoid dark shots and save storage." },
                                            { label: "PTZ Action", desc: "Camera positioning behavior before each shot: Current Angle, Patrol Tour (cycles presets), or Return to Home." }
                                        ]}
                                    />
                                </Typography>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 0.8 }}>
                                    {scheduleSaving && <CircularProgress size={12} sx={{ color: "#ffcc33" }} />}
                                    <span style={{ fontSize: "11px", color: enableWidelapse ? "#4caf50" : "#888", fontWeight: 600 }}>
                                        {enableWidelapse ? "Enabled" : "Disabled"}
                                    </span>
                                    <Switch
                                        size="small"
                                        checked={enableWidelapse}
                                        onChange={(e) => {
                                            const val = e.target.checked;
                                            setEnableWidelapse(val);
                                            handleSaveSchedule(val, scheduleInterval, schedulePtzMode);
                                        }}
                                        sx={{
                                            "& .MuiSwitch-switchBase.Mui-checked": { color: "#4caf50" },
                                            "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { backgroundColor: "#4caf50" }
                                        }}
                                    />
                                </Box>
                            </Box>

                            <Box className="card-data-table">
                                <Box className="data-row">
                                    <span className="data-label">Interval</span>
                                    <Select
                                        size="small"
                                        value={scheduleInterval}
                                        onChange={(e) => {
                                            const val = e.target.value;
                                            setScheduleInterval(val);
                                            handleSaveSchedule(enableWidelapse, val, schedulePtzMode);
                                        }}
                                        className="schedule-inline-select"
                                    >
                                        <MenuItem value="10">10 Minutes</MenuItem>
                                        <MenuItem value="15">15 Minutes</MenuItem>
                                        <MenuItem value="30">30 Minutes</MenuItem>
                                        <MenuItem value="60">60 Minutes</MenuItem>
                                    </Select>
                                </Box>
                                <Box className="data-row">
                                    <span className="data-label">Time Window</span>
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                        <Select
                                            size="small"
                                            value={startTime}
                                            onChange={(e) => setStartTime(e.target.value)}
                                            className="schedule-inline-select"
                                            sx={{ minWidth: "75px !important" }}
                                        >
                                            <MenuItem value="06:00">06:00 AM</MenuItem>
                                            <MenuItem value="07:00">07:00 AM</MenuItem>
                                            <MenuItem value="08:00">08:00 AM</MenuItem>
                                        </Select>
                                        <span style={{ fontSize: "10px", color: "#888" }}>to</span>
                                        <Select
                                            size="small"
                                            value={endTime}
                                            onChange={(e) => setEndTime(e.target.value)}
                                            className="schedule-inline-select"
                                            sx={{ minWidth: "75px !important" }}
                                        >
                                            <MenuItem value="17:00">05:00 PM</MenuItem>
                                            <MenuItem value="18:00">06:00 PM</MenuItem>
                                            <MenuItem value="19:00">07:00 PM</MenuItem>
                                        </Select>
                                    </Box>
                                </Box>
                                <Box className="data-row">
                                    <span className="data-label">PTZ Schedule Action</span>
                                    <Select
                                        size="small"
                                        value={schedulePtzMode}
                                        onChange={(e) => {
                                            const val = e.target.value;
                                            setSchedulePtzMode(val);
                                            handleSaveSchedule(enableWidelapse, scheduleInterval, val);
                                        }}
                                        className="schedule-inline-select"
                                    >
                                        <MenuItem value="current">Current Angle</MenuItem>
                                        <MenuItem value="tour">Patrol Tour (All Presets)</MenuItem>
                                        <MenuItem value="home">Return to Home</MenuItem>
                                    </Select>
                                </Box>
                            </Box>
                        </Box>

                        {/* Card 3: Storage */}
                        <Box className="ptz-info-card">
                            <Typography className="card-heading" sx={{ display: "inline-flex", alignItems: "center", gap: 0.8 }}>
                                Storage
                                <CardInfoTooltip
                                    title="Storage Allocation & Cloud Retention"
                                    purpose="Monitors local camera MicroSD edge capacity and centralized cloud timelapse archive usage."
                                    details={[
                                        { label: "SD Card (Camera)", desc: "High-speed onboard MicroSD edge storage for offline backup caching during network drops." },
                                        { label: "Cloud Storage", desc: "Centralized cloud storage consumed by high-resolution timelapse photos and daily videos." }
                                    ]}
                                />
                            </Typography>
                            <Box sx={{ mt: 1 }}>
                                <Tooltip title="Local MicroSD card storage in camera for offline backup and frame cache" arrow placement="top">
                                    <Box>
                                        <Box sx={{ display: "flex", justifyContent: "space-between", fontSize: "11px", mb: 0.5 }}>
                                            <span style={{ color: "#aaa" }}>SD Card (Camera)</span>
                                            <span style={{ fontWeight: 700, color: "#ffcc33" }}>36%</span>
                                        </Box>
                                        <LinearProgress
                                            variant="determinate"
                                            value={36}
                                            sx={{
                                                height: 6,
                                                borderRadius: 3,
                                                backgroundColor: "rgba(255,255,255,0.1)",
                                                "& .MuiLinearProgress-bar": { backgroundColor: "#ffcc33" }
                                            }}
                                        />
                                        <Typography sx={{ fontSize: "10px", color: "#777", mt: 0.4, mb: 1.5 }}>
                                            182 GB / 512 GB
                                        </Typography>
                                    </Box>
                                </Tooltip>

                                <Tooltip title="Cloud storage capacity allocated for this project's photos and daily recap videos" arrow placement="top">
                                    <Box>
                                        <Box sx={{ display: "flex", justifyContent: "space-between", fontSize: "11px", mb: 0.5 }}>
                                            <span style={{ color: "#aaa" }}>Cloud Storage</span>
                                            <span style={{ fontWeight: 700, color: "#4fc3f7" }}>24%</span>
                                        </Box>
                                        <LinearProgress
                                            variant="determinate"
                                            value={24}
                                            sx={{
                                                height: 6,
                                                borderRadius: 3,
                                                backgroundColor: "rgba(255,255,255,0.1)",
                                                "& .MuiLinearProgress-bar": { backgroundColor: "#4fc3f7" }
                                            }}
                                        />
                                        <Typography sx={{ fontSize: "10px", color: "#777", mt: 0.4 }}>
                                            1.2 TB / 5 TB
                                        </Typography>
                                    </Box>
                                </Tooltip>
                            </Box>
                        </Box>
                    </Box>
                </Box>

                {/* Right Column: PTZ Controller & Sidebar */}
                <Box className="ptz-station-sidebar">
                    {/* Section 1: PTZ Control (Joystick / D-Pad) */}
                    <Box className="ptz-panel-section">
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.5 }}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.8 }}>
                                <Typography className="sidebar-section-title">PTZ Control</Typography>
                                <CardInfoTooltip
                                    title="PTZ Direction & Zoom Controls"
                                    purpose="Continuous 8-way directional pan, tilt, and optical zoom targeting with selectable motor speeds."
                                    details={[
                                        { label: "D-Pad Directional Arrows", desc: "Pan left/right (360° continuous rotation) and tilt up/down to orient the camera." },
                                        { label: "Center Reset (Home)", desc: "Instantly re-centers the camera optics back to baseline home orientation." },
                                        { label: "Motor Speed Mode", desc: "Select Slow for micro framing, Medium for standard tracking, or Fast for wide sweeps." },
                                        { label: "Optical Zoom & Focus", desc: "Telephoto zoom in/out with automatic autofocus stabilization." }
                                    ]}
                                />
                            </Box>
                            <Box className="speed-toggle-group">
                                <span className="speed-label">Speed</span>
                                <Tooltip title="Slow precision speed for fine framing adjustments" arrow placement="top">
                                    <button
                                        className={`speed-pill ${speed === "slow" ? "active" : ""}`}
                                        onClick={() => setSpeed("slow")}
                                    >
                                        Slow
                                    </button>
                                </Tooltip>
                                <Tooltip title="Standard balanced speed for normal camera panning and tilting" arrow placement="top">
                                    <button
                                        className={`speed-pill ${speed === "medium" ? "active" : ""}`}
                                        onClick={() => setSpeed("medium")}
                                    >
                                        Medium
                                    </button>
                                </Tooltip>
                                <Tooltip title="Fast speed for quick site sweeps and wide repositioning" arrow placement="top">
                                    <button
                                        className={`speed-pill ${speed === "fast" ? "active" : ""}`}
                                        onClick={() => setSpeed("fast")}
                                    >
                                        Fast
                                    </button>
                                </Tooltip>
                            </Box>
                        </Box>

                        {/* Joystick & Side Buttons Container */}
                        <Box className="dpad-and-zoom-row">
                            {/* Circular D-Pad */}
                            <Box className={`ptz-dpad-circle ${ptzActionLoading ? "is-loading" : ""}`}>
                                {ptzActionLoading && <div className="ptz-dpad-spinning-ring" />}
                                <Tooltip title="Tilt camera upward" arrow placement="top">
                                    <button
                                        className="dpad-btn up"
                                        onClick={() => handlePtz("up")}
                                    >
                                        <FaArrowUp size={15} />
                                    </button>
                                </Tooltip>
                                <Tooltip title="Pan camera left" arrow placement="left">
                                    <button
                                        className="dpad-btn left"
                                        onClick={() => handlePtz("left")}
                                    >
                                        <FaArrowLeft size={15} />
                                    </button>
                                </Tooltip>
                                <Tooltip title="Return camera to default Home position" arrow placement="top">
                                    <button
                                        className="dpad-btn center-home"
                                        onClick={() => handlePtz("home")}
                                    >
                                        <FaHouse size={16} />
                                    </button>
                                </Tooltip>
                                <Tooltip title="Pan camera right" arrow placement="right">
                                    <button
                                        className="dpad-btn right"
                                        onClick={() => handlePtz("right")}
                                    >
                                        <FaArrowRight size={15} />
                                    </button>
                                </Tooltip>
                                <Tooltip title="Tilt camera downward" arrow placement="bottom">
                                    <button
                                        className="dpad-btn down"
                                        onClick={() => handlePtz("down")}
                                    >
                                        <FaArrowDown size={15} />
                                    </button>
                                </Tooltip>
                            </Box>

                            {/* Vertical Zoom & Focus Controls */}
                            <Box className="vertical-zoom-col">
                                <Box className="btn-stack-group">
                                    <Tooltip title="Zoom lens in for closer detail" arrow placement="right">
                                        <button
                                            className="stack-control-btn top"
                                            onClick={() => handlePtz("zoom-in")}
                                        >
                                            <FaPlus size={13} />
                                        </button>
                                    </Tooltip>
                                    <Tooltip title="Zoom lens out for wider overview" arrow placement="right">
                                        <button
                                            className="stack-control-btn bottom"
                                            onClick={() => handlePtz("zoom-out")}
                                        >
                                            <span style={{ fontSize: 18, lineHeight: 1 }}>−</span>
                                        </button>
                                    </Tooltip>
                                    <span className="stack-label">Zoom</span>
                                </Box>

                                <Box className="btn-stack-group" sx={{ mt: 1 }}>
                                    <Tooltip title="Adjust focus nearer" arrow placement="right">
                                        <button
                                            className="stack-control-btn top"
                                            onClick={() => handlePtz("focus-in")}
                                        >
                                            <FaCrosshairs size={13} />
                                        </button>
                                    </Tooltip>
                                    <Tooltip title="Adjust focus farther" arrow placement="right">
                                        <button
                                            className="stack-control-btn bottom"
                                            onClick={() => handlePtz("focus-out")}
                                        >
                                            <FaSliders size={12} />
                                        </button>
                                    </Tooltip>
                                    <span className="stack-label">Focus</span>
                                </Box>
                            </Box>
                        </Box>

                        {/* 4 Quick PTZ buttons below D-pad */}
                        <Box className="ptz-quick-grid">
                            <Tooltip title="Zoom lens out" arrow placement="top">
                                <button
                                    className="quick-action-pill"
                                    onClick={() => handlePtz("zoom-out")}
                                >
                                    <FaMagnifyingGlassMinus size={12} style={{ marginRight: 6 }} />
                                    Zoom Out
                                </button>
                            </Tooltip>
                            <Tooltip title="Zoom lens in" arrow placement="top">
                                <button
                                    className="quick-action-pill"
                                    onClick={() => handlePtz("zoom-in")}
                                >
                                    <FaMagnifyingGlassPlus size={12} style={{ marginRight: 6 }} />
                                    Zoom In
                                </button>
                            </Tooltip>
                            <Tooltip title="Trigger automatic lens focus calibration" arrow placement="top">
                                <button
                                    className="quick-action-pill active-blue"
                                    onClick={() => handlePtz("autofocus")}
                                >
                                    <FaGear size={12} style={{ marginRight: 6 }} />
                                    Auto Focus
                                </button>
                            </Tooltip>
                            <Tooltip title="Return camera to primary Home position" arrow placement="top">
                                <button
                                    className="quick-action-pill"
                                    onClick={() => handlePtz("home")}
                                >
                                    <FaHouse size={12} style={{ marginRight: 6 }} />
                                    Home
                                </button>
                            </Tooltip>
                        </Box>
                    </Box>

                    {/* Section 2: Presets Grid - ZERO FLICKERING & GUARANTEED CRISP THUMBNAILS */}
                    <Box className="ptz-panel-section">
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.8 }}>
                                <Typography className="sidebar-section-title">Presets</Typography>
                                <CardInfoTooltip
                                    title="Saved Preset Viewpoints"
                                    purpose="Quick-switch viewing points. Clicking any preset smoothly pans/tilts the camera and updates the snapshot preview without flickering."
                                    details={[
                                        { label: "Preset Cards", desc: "Select any angle to trigger fast motorized PTZ repositioning." },
                                        { label: "Set Current as Preset", desc: "Registers current camera angle and zoom level as a new named position." },
                                        { label: "Go to Preset", desc: "Commands PTZ motors to target the currently highlighted preset." },
                                        { label: "Zero-Flicker Preload", desc: "Silently fetches live frame into an off-screen buffer before rendering to avoid screen flashing." }
                                    ]}
                                />
                            </Box>
                            <button
                                className="link-action-btn"
                                onClick={() => setIsAddPresetOpen(true)}
                            >
                                Manage
                            </button>
                        </Box>

                        <Box className="presets-grid-container">
                            {presetsList.slice(0, 6).map((item, idx) => {
                                const isCurrent = activePreset === item.preset_name;
                                return (
                                    <Box
                                        key={item.preset_name || idx}
                                        className={`preset-card-item ${isCurrent ? "active-preset" : ""}`}
                                        onClick={() => handleGotoPreset(item.preset_name)}
                                        title={`Go to ${item.preset_name}`}
                                    >
                                        <Box className="preset-card-img-wrap">
                                            <img
                                                src={getPresetThumbnail(item)}
                                                alt={item.preset_name}
                                                className="preset-card-thumb"
                                                onError={(e) => {
                                                    e.target.onerror = null;
                                                    e.target.src = getPresetFallbackSvg(item.preset_name);
                                                }}
                                            />
                                            {loadingPresetImage === item.preset_name && (
                                                <Box className="preset-thumb-loading-badge">
                                                    <CircularProgress size={12} sx={{ color: "#ffcc33" }} />
                                                    <span>Capturing...</span>
                                                </Box>
                                            )}
                                            {isCurrent && (
                                                <span className="preset-current-indicator">Active</span>
                                            )}
                                        </Box>
                                        <Typography className="preset-card-name" noWrap>
                                            {item.preset_name}
                                        </Typography>
                                    </Box>
                                );
                            })}
                        </Box>

                        <Box className="preset-actions-row">
                            <Tooltip title="Save the current camera angle and zoom level as a new preset position" arrow placement="top">
                                <button
                                    className="preset-action-bar-btn"
                                    onClick={() => setIsAddPresetOpen(true)}
                                >
                                    <FaPlus size={11} style={{ marginRight: 6 }} />
                                    New Preset
                                </button>
                            </Tooltip>
                            <Tooltip title={`Update active preset "${activePreset}" with current position & save permanently for this camera`} arrow placement="top">
                                <button
                                    className="preset-action-bar-btn"
                                    onClick={() => handleUpdatePreset(activePreset)}
                                >
                                    <FaCrosshairs size={10} style={{ marginRight: 6 }} />
                                    Update Active
                                </button>
                            </Tooltip>
                            <Tooltip title={`Rotate camera to selected preset: ${activePreset}`} arrow placement="top">
                                <button
                                    className="preset-action-bar-btn"
                                    onClick={() => handleGotoPreset(activePreset)}
                                >
                                    <FaPlay size={10} style={{ marginRight: 6 }} />
                                    Go to Preset
                                </button>
                            </Tooltip>
                        </Box>
                    </Box>

                    {/* Section 3: Quick Actions */}
                    <Box className="ptz-panel-section">
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.8, mb: 1.2 }}>
                            <Typography className="sidebar-section-title">Quick Actions</Typography>
                            <CardInfoTooltip
                                title="Quick Station Actions"
                                purpose="One-click operations for manual snapshot downloads, automated patrol sweeps, and home repositioning."
                                details={[
                                    { label: "Capture Image", desc: "Immediately downloads a pristine full-resolution snapshot from live camera feed." },
                                    { label: "Start / Stop Tour", desc: "Begins or halts automated cyclic patrol tour through all saved presets." },
                                    { label: "Home", desc: "Quickly commands camera motors back to baseline home perspective." }
                                ]}
                            />
                        </Box>
                        <Box className="quick-tiles-grid">
                            <Tooltip title="Immediately capture a high-resolution snapshot and download it to your device" arrow placement="top">
                                <button
                                    className="quick-tile-btn"
                                    onClick={handleDownloadSnapshot}
                                >
                                    <FaDownload size={15} />
                                    <span>Capture Image</span>
                                </button>
                            </Tooltip>
                            <Tooltip title={isTouring ? "Patrol tour active - click to stop" : "Start automated patrol cycling through all saved preset angles"} arrow placement="top">
                                <button
                                    className={`quick-tile-btn ${isTouring ? "active-tour" : ""}`}
                                    onClick={handleToggleTour}
                                >
                                    <FaRotate size={15} className={isTouring ? "spinning" : ""} />
                                    <span>{isTouring ? "Touring..." : "Start Tour"}</span>
                                </button>
                            </Tooltip>
                            <Tooltip title="Stop ongoing patrol tour and freeze camera position" arrow placement="top">
                                <button
                                    className="quick-tile-btn"
                                    onClick={() => {
                                        if (isTouring && tourTimerRef.current) {
                                            clearInterval(tourTimerRef.current);
                                            tourTimerRef.current = null;
                                        }
                                        setIsTouring(false);
                                        handlePtz("stop");
                                    }}
                                >
                                    <FaStop size={15} />
                                    <span>Stop Tour</span>
                                </button>
                            </Tooltip>
                            <Tooltip title="Re-center camera directly to default Home position (WIDELAPSE_HOME)" arrow placement="top">
                                <button
                                    className="quick-tile-btn"
                                    onClick={() => handlePtz("home")}
                                >
                                    <FaHouse size={15} />
                                    <span>Return to Home</span>
                                </button>
                            </Tooltip>
                        </Box>
                    </Box>
                </Box>
            </Box>
            )}

            {/* =========================================================================
                TAB 2: PRESET VIEW
               ========================================================================= */}
            {activeTopTab === "preset" && (
                <Box className="ptz-station-body">
                    <Box className="ptz-station-main-col">
                        {/* Live Feed during Preset Mode */}
                        <Box className="ptz-video-container" ref={videoContainerRef}>
                            {/* 1. Fast Initial JPEG / MJPEG Stream */}
                            {mjpegSrc && (
                                <img
                                    key={`preset_mjpeg_${streamKey}`}
                                    src={`${mjpegSrc}${mjpegSrc.includes("?") ? "&" : "?"}_t=${streamKey}`}
                                    alt="Preset JPEG Stream"
                                    crossOrigin="anonymous"
                                    className="ptz-stream-media"
                                    onLoad={() => setIsPresetMediaLoading(false)}
                                    onError={() => {
                                        setIsPresetMediaLoading(false);
                                    }}
                                    ref={(el) => {
                                        if (el && el.complete && el.naturalWidth > 0 && isPresetMediaLoading) {
                                            setIsPresetMediaLoading(false);
                                        }
                                    }}
                                    style={{
                                        position: "absolute",
                                        top: 0,
                                        left: 0,
                                        width: "100%",
                                        height: "100%",
                                        objectFit: "contain",
                                        opacity: mediaStreamReady ? 0 : 1,
                                        transition: "opacity 0.4s ease-in-out",
                                        pointerEvents: mediaStreamReady ? "none" : "auto",
                                        zIndex: mediaStreamReady ? 1 : 2,
                                    }}
                                />
                            )}

                            {/* 2. Continuous Media Streaming (H.264 / MP4 video encoder) */}
                            {mediaSrc && (
                                <video
                                    key={`preset_media_${streamKey}`}
                                    src={`${mediaSrc}${mediaSrc.includes("?") ? "&" : "?"}_t=${streamKey}`}
                                    crossOrigin="anonymous"
                                    muted
                                    playsInline
                                    autoPlay
                                    className="ptz-stream-media"
                                    onCanPlay={() => {
                                        setMediaStreamReady(true);
                                        setIsPresetMediaLoading(false);
                                    }}
                                    onPlaying={() => {
                                        setMediaStreamReady(true);
                                        setIsPresetMediaLoading(false);
                                    }}
                                    onError={() => {
                                        setMediaStreamReady(false);
                                    }}
                                    style={{
                                        position: "absolute",
                                        top: 0,
                                        left: 0,
                                        width: "100%",
                                        height: "100%",
                                        objectFit: "contain",
                                        opacity: mediaStreamReady ? 1 : 0,
                                        transition: "opacity 0.4s ease-in-out",
                                        pointerEvents: mediaStreamReady ? "auto" : "none",
                                        zIndex: mediaStreamReady ? 2 : 1,
                                    }}
                                />
                            )}

                            {!mjpegSrc && !mediaSrc && (
                                <Box className="ptz-stream-placeholder">
                                    <CircularProgress size={40} sx={{ color: "#ffcc33" }} />
                                    <Typography sx={{ mt: 1.5, color: "#aaa", fontSize: 13 }}>
                                        Connecting to preset view...
                                    </Typography>
                                </Box>
                            )}

                            {isPresetMediaLoading && (
                                <Box className="ptz-centered-media-loader">
                                    <CircularProgress size={44} sx={{ color: "#ffcc33", mb: 1.5 }} thickness={4} />
                                    <Typography sx={{ color: "#ffcc33", fontSize: "13px", fontWeight: 700, letterSpacing: "0.5px" }}>
                                        Loading Preset: {activePreset}...
                                    </Typography>
                                </Box>
                            )}
                            <Box className="ptz-hud-top-left">
                                <Typography className="hud-text-time">{currentTimeStr}</Typography>
                                <Typography className="hud-text-model">
                                    Preset View: {activePreset}
                                </Typography>
                            </Box>
                            <Box className="ptz-hud-top-right">
                                <Tooltip title="Capture Snapshot">
                                    <button className="hud-action-btn" onClick={() => handleDownloadSnapshot()}>
                                        <FaCamera size={13} />
                                    </button>
                                </Tooltip>
                            </Box>
                            <Box className="ptz-hud-bottom-left">
                                <span className="preset-hud-tag">
                                    Active: <strong>{activePreset}</strong>
                                </span>
                                {isTouring && (
                                    <span className="ptz-hud-tour-badge">
                                        <FaRotate size={10} className="spinning" style={{ marginRight: 5 }} />
                                        Tour Active
                                    </span>
                                )}
                            </Box>
                        </Box>

                        {/* Presets Manager Grid Card */}
                        <Box className="ptz-presets-manager-card">
                            <Box className="preset-view-controls-bar">
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                    <Typography sx={{ fontSize: 15, fontWeight: 700, color: "var(--ptz-text-main)", display: "flex", alignItems: "center", gap: 1 }}>
                                        <FaCrosshairs color="#ffcc33" /> Preset Positions Manager ({presetsList.length})
                                    </Typography>
                                    <CardInfoTooltip
                                        title="Preset Positions Manager"
                                        purpose="Central visual console for managing all saved PTZ camera stations and automated tour routines."
                                        details={[
                                            { label: "Preset Cards Grid", desc: "Displays each position with directional compass angle and live thumbnail. Click to navigate." },
                                            { label: "Add Current Position", desc: "Registers current Pan, Tilt, and Zoom coordinates as a new preset." },
                                            { label: "Patrol Tour", desc: "Cycles automatically through all saved presets with configured dwell intervals." },
                                            { label: "Update Position", desc: "Overwrites selected preset coordinates with current camera perspective." },
                                            { label: "Zero-Flicker Preload", desc: "Silently preloads fresh snapshot off-screen before displaying to eliminate screen flashing." }
                                        ]}
                                    />
                                </Box>
                                <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
                                    <Tooltip title="Save current camera angle, pan/tilt position, and zoom as a new preset" arrow placement="top">
                                        <button className="preset-action-bar-btn" onClick={() => setIsAddPresetOpen(true)}>
                                            <FaPlus size={11} style={{ marginRight: 6 }} /> Add Current Position
                                        </button>
                                    </Tooltip>
                                    <Tooltip title={isTouring ? "Stop active patrol tour" : "Automatically patrol and tour through all defined preset angles with dwell time"} arrow placement="top">
                                        <button
                                            className={`preset-action-bar-btn ${isTouring ? "active-tour" : ""}`}
                                            onClick={handleToggleTour}
                                        >
                                            <FaRotate size={11} className={isTouring ? "spinning" : ""} style={{ marginRight: 6 }} />
                                            {isTouring ? "Stop Patrol Tour" : "Start Patrol Tour"}
                                        </button>
                                    </Tooltip>
                                    <Tooltip title="Return camera directly to default Home position" arrow placement="top">
                                        <button className="preset-action-bar-btn" onClick={() => handlePtz("home")}>
                                            <FaHouse size={11} style={{ marginRight: 6 }} /> Home
                                        </button>
                                    </Tooltip>
                                </Box>
                            </Box>

                            <Box className="ptz-preset-cards-grid">
                                {presetsList.map((preset) => {
                                    const isCur = activePreset === preset.preset_name;
                                    return (
                                        <Box
                                            key={preset.preset_name}
                                            className={`ptz-preset-card-full ${isCur ? "active" : ""}`}
                                            onClick={() => handleGotoPreset(preset.preset_name)}
                                        >
                                            <Box className="ptz-preset-card-thumb-box">
                                                <img
                                                    src={preset.image || getPresetFallbackSvg(preset.preset_name)}
                                                    alt={preset.preset_name}
                                                    onError={(e) => {
                                                        e.target.onerror = null;
                                                        e.target.src = getPresetFallbackSvg(preset.preset_name);
                                                    }}
                                                />
                                                {loadingPresetImage === preset.preset_name && (
                                                    <Box className="preset-thumb-loading-badge">
                                                        <CircularProgress size={12} sx={{ color: "#ffcc33" }} />
                                                        <span>Capturing...</span>
                                                    </Box>
                                                )}
                                                {isCur && <span className="preset-current-indicator">ACTIVE</span>}
                                            </Box>
                                            <Box className="ptz-preset-card-body">
                                                <Typography className="ptz-preset-card-title">
                                                    {preset.preset_name}
                                                </Typography>
                                                <Box className="ptz-preset-card-actions">
                                                    <Tooltip title={`Rotate camera to ${preset.preset_name}`} arrow placement="top">
                                                        <button
                                                            className="ptz-preset-btn"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleGotoPreset(preset.preset_name);
                                                            }}
                                                        >
                                                            <FaPlay size={9} /> Go To
                                                        </button>
                                                    </Tooltip>
                                                    <Tooltip title={`Update & permanently save "${preset.preset_name}" with camera's current angle and live image`} arrow placement="top">
                                                        <button
                                                            className="ptz-preset-btn"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleUpdatePreset(preset.preset_name);
                                                            }}
                                                        >
                                                            <FaCrosshairs size={9} /> Update
                                                        </button>
                                                    </Tooltip>
                                                </Box>
                                            </Box>
                                        </Box>
                                    );
                                })}
                            </Box>
                        </Box>
                    </Box>

                    {/* Right Column: PTZ Control for Aiming & Quick Actions */}
                    <Box className="ptz-station-sidebar">
                        <Box className="ptz-panel-section">
                            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.5 }}>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 0.8 }}>
                                    <Typography className="sidebar-section-title">PTZ Calibration</Typography>
                                    <CardInfoTooltip
                                        title="PTZ Calibration"
                                        purpose="Fine-tune camera alignment and field of view before recording or saving presets."
                                        details={[
                                            { label: "Precision D-Pad", desc: "Allows step-by-step or continuous movement across Pan and Tilt axes." },
                                            { label: "Home Orientation", desc: "Re-centers the camera optics back to the baseline zero position." },
                                            { label: "Step Speed Adjustment", desc: "Toggle motor responsiveness (Slow, Medium, Fast) for micro-adjustments." },
                                            { label: "Optical Zoom Stack", desc: "Zoom In/Out buttons for telephoto focal calibration." }
                                        ]}
                                    />
                                </Box>
                                <Box className="speed-toggle-group">
                                    <span className="speed-label">Speed</span>
                                    <button className={`speed-pill ${speed === "slow" ? "active" : ""}`} onClick={() => setSpeed("slow")}>Slow</button>
                                    <button className={`speed-pill ${speed === "medium" ? "active" : ""}`} onClick={() => setSpeed("medium")}>Med</button>
                                    <button className={`speed-pill ${speed === "fast" ? "active" : ""}`} onClick={() => setSpeed("fast")}>Fast</button>
                                </Box>
                            </Box>

                            <Box className="dpad-and-zoom-row">
                                <Box className={`ptz-dpad-circle ${ptzActionLoading ? "is-loading" : ""}`}>
                                    {ptzActionLoading && <div className="ptz-dpad-spinning-ring" />}
                                    <button className="dpad-btn up" title="Tilt Up" onClick={() => handlePtz("up")}><FaArrowUp size={15} /></button>
                                    <button className="dpad-btn left" title="Pan Left" onClick={() => handlePtz("left")}><FaArrowLeft size={15} /></button>
                                    <button className="dpad-btn center-home" title="Center / Home" onClick={() => handlePtz("home")}><FaHouse size={16} /></button>
                                    <button className="dpad-btn right" title="Pan Right" onClick={() => handlePtz("right")}><FaArrowRight size={15} /></button>
                                    <button className="dpad-btn down" title="Tilt Down" onClick={() => handlePtz("down")}><FaArrowDown size={15} /></button>
                                </Box>
                                <Box className="vertical-zoom-col">
                                    <Box className="btn-stack-group">
                                        <button className="stack-control-btn top" title="Zoom In" onClick={() => handlePtz("zoom-in")}><FaPlus size={13} /></button>
                                        <button className="stack-control-btn bottom" title="Zoom Out" onClick={() => handlePtz("zoom-out")}><span style={{ fontSize: 18, lineHeight: 1 }}>−</span></button>
                                        <span className="stack-label">Zoom</span>
                                    </Box>
                                </Box>
                            </Box>
                        </Box>

                        <Box className="ptz-panel-section">
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.8, mb: 1.2 }}>
                                <Typography className="sidebar-section-title">Patrol Actions</Typography>
                                <CardInfoTooltip
                                    title="Patrol & Quick Tour Operations"
                                    purpose="Automated sequencing and manual override actions for patrol tours."
                                    details={[
                                        { label: "New Preset", desc: "Registers current position as a new preset coordinate." },
                                        { label: "Start/Stop Tour", desc: "Initiates or halts automated cycling across all presets with configured dwell times." },
                                        { label: "Home", desc: "Instantly commands camera to baseline Home position." }
                                    ]}
                                />
                            </Box>
                            <Box className="quick-tiles-grid">
                                <button className="quick-tile-btn" onClick={() => setIsAddPresetOpen(true)}>
                                    <FaPlus size={15} />
                                    <span>New Preset</span>
                                </button>
                                <button className={`quick-tile-btn ${isTouring ? "active-tour" : ""}`} onClick={handleToggleTour}>
                                    <FaRotate size={15} className={isTouring ? "spinning" : ""} />
                                    <span>{isTouring ? "Touring..." : "Start Tour"}</span>
                                </button>
                                <button className="quick-tile-btn" onClick={() => {
                                    if (isTouring && tourTimerRef.current) {
                                        clearInterval(tourTimerRef.current);
                                        tourTimerRef.current = null;
                                    }
                                    setIsTouring(false);
                                    handlePtz("stop");
                                }}>
                                    <FaStop size={15} />
                                    <span>Stop Tour</span>
                                </button>
                                <button className="quick-tile-btn" onClick={() => handlePtz("home")}>
                                    <FaHouse size={15} />
                                    <span>Home</span>
                                </button>
                            </Box>
                        </Box>
                    </Box>
                </Box>
            )}

            {/* =========================================================================
                TAB 3: WIDELAPSE VIEW
               ========================================================================= */}
            {activeTopTab === "widelapse" && (
                <Box className="ptz-station-body">
                    <Box className="ptz-station-main-col">
                        {/* Widelapse High-Resolution Panorama Hero */}
                        <Box className="ptz-widelapse-hero-card">
                            <Box className="ptz-widelapse-hero-header">
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
                                    <Typography sx={{ fontSize: 16, fontWeight: 700, color: "var(--ptz-text-main)" }}>
                                        Widelapse High-Resolution Panorama
                                    </Typography>
                                    <span className="preset-hud-tag">Slot: <strong>{selectedTimeSlot}</strong></span>
                                    <span className="preset-hud-tag">Date: <strong>{selectedDate}</strong></span>
                                    <span className="preset-hud-tag">Res: <strong>{camera?.image_resolution || "3840 x 2160 (4K)"}</strong></span>
                                </Box>
                                <Box sx={{ display: "flex", gap: 1 }}>
                                    <Button
                                        size="small"
                                        variant="contained"
                                        onClick={() => handleDownloadSnapshot(camera?.thumbnail || camera?.image_url, `Widelapse_${selectedDate}_${selectedTimeSlot}.jpg`)}
                                        startIcon={<FaDownload size={12} />}
                                        sx={{
                                            background: "#ffcc33",
                                            color: "#000",
                                            fontWeight: 700,
                                            fontSize: "11px",
                                            textTransform: "none",
                                            borderRadius: "6px",
                                            "&:hover": { background: "#ffd65c" }
                                        }}
                                    >
                                        Download Image
                                    </Button>
                                </Box>
                            </Box>

                            <Box className="ptz-widelapse-img-container" sx={{ position: "relative" }}>
                                <img
                                    src={camera?.thumbnail || camera?.image_url || getPresetFallbackSvg(selectedTimeSlot)}
                                    alt="Widelapse Panorama"
                                    crossOrigin="anonymous"
                                    onLoad={() => setIsWidelapseMediaLoading(false)}
                                    onError={(e) => {
                                        setIsWidelapseMediaLoading(false);
                                        e.target.onerror = null;
                                        e.target.src = getPresetFallbackSvg(selectedTimeSlot);
                                    }}
                                />
                                {isWidelapseMediaLoading && (
                                    <Box className="ptz-centered-media-loader">
                                        <CircularProgress size={44} sx={{ color: "#ffcc33", mb: 1.5 }} thickness={4} />
                                        <Typography sx={{ color: "#ffcc33", fontSize: "13px", fontWeight: 700, letterSpacing: "0.5px" }}>
                                            Loading Panorama Image...
                                        </Typography>
                                    </Box>
                                )}
                            </Box>
                        </Box>

                        {/* Timeline & Carousel for Widelapse captures */}
                        <Box className="ptz-timeline-card">
                            <Box className="ptz-timeline-header">
                                <Typography sx={{ fontSize: 13, fontWeight: 600, color: "var(--ptz-text-main)" }}>
                                    Select Widelapse Capture Time
                                </Typography>
                                <Box className="ptz-timeline-date-selector">
                                    <FaCalendarDays size={13} className="cal-icon" />
                                    <input
                                        type="date"
                                        className="ptz-date-input"
                                        value={selectedDate}
                                        onChange={(e) => setSelectedDate(e.target.value)}
                                    />
                                    <button className="date-nav-btn" onClick={() => {
                                        const d = new Date(selectedDate);
                                        d.setDate(d.getDate() - 1);
                                        setSelectedDate(d.toISOString().split("T")[0]);
                                    }}>
                                        <FaChevronLeft size={10} />
                                    </button>
                                    <button className="date-nav-btn" onClick={() => {
                                        const d = new Date(selectedDate);
                                        d.setDate(d.getDate() + 1);
                                        setSelectedDate(d.toISOString().split("T")[0]);
                                    }}>
                                        <FaChevronRight size={10} />
                                    </button>
                                </Box>
                            </Box>

                            <Box className="ptz-timeline-strip-wrapper">
                                <button className="strip-arrow-btn left"><FaChevronLeft size={12} /></button>
                                <Box className="ptz-timeline-strip">
                                    {TIME_SLOTS.map((slot) => {
                                        const isSelected = selectedTimeSlot === slot.time;
                                        return (
                                            <Box
                                                key={slot.time}
                                                className={`timeline-slot-item ${isSelected ? "selected" : ""}`}
                                                onClick={() => setSelectedTimeSlot(slot.time)}
                                            >
                                                <Box className="slot-thumb-container">
                                                    <img
                                                        src={camera?.thumbnail || camera?.image_url || getPresetFallbackSvg(slot.time)}
                                                        alt={slot.time}
                                                        className="slot-thumb-img"
                                                        onError={(e) => {
                                                            e.target.onerror = null;
                                                            e.target.src = getPresetFallbackSvg(slot.time);
                                                        }}
                                                    />
                                                    {isSelected && <span className="slot-active-dot"></span>}
                                                </Box>
                                                <span className="slot-time-text">{slot.time}</span>
                                            </Box>
                                        );
                                    })}
                                </Box>
                                <button className="strip-arrow-btn right"><FaChevronRight size={12} /></button>
                            </Box>
                        </Box>
                    </Box>

                    {/* Right Column: Widelapse Controls & Schedule */}
                    <Box className="ptz-station-sidebar">
                        <Box className="ptz-panel-section">
                            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.5 }}>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 0.8 }}>
                                    <Typography className="sidebar-section-title">Widelapse Automation</Typography>
                                    <CardInfoTooltip
                                        title="Widelapse Automation Schedule"
                                        purpose="Configures automated background capture cycles, scheduled intervals, and PTZ orientation routines."
                                        details={[
                                            { label: "Schedule Status", desc: "Activates or pauses automated periodic high-resolution timelapse captures." },
                                            { label: "Capture Interval", desc: "Duration between shots (10 min, 15 min, 30 min, or 60 min)." },
                                            { label: "PTZ Schedule Mode", desc: "Positioning mode before shooting: Current Angle, Patrol Tour, or Return to Home." },
                                            { label: "Manual Trigger", desc: "Immediately executes a photo capture and syncs to cloud storage." }
                                        ]}
                                    />
                                </Box>
                            </Box>
                            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                    <SettingLabel label="Schedule Status" />
                                    <Switch
                                        size="small"
                                        checked={enableWidelapse}
                                        onChange={(e) => {
                                            const val = e.target.checked;
                                            setEnableWidelapse(val);
                                            handleSaveSchedule(val, scheduleInterval, schedulePtzMode);
                                        }}
                                    />
                                </Box>
                                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                    <SettingLabel label="Interval" />
                                    <Select
                                        size="small"
                                        value={scheduleInterval}
                                        onChange={(e) => {
                                            const val = e.target.value;
                                            setScheduleInterval(val);
                                            handleSaveSchedule(enableWidelapse, val, schedulePtzMode);
                                        }}
                                        className="schedule-inline-select"
                                    >
                                        <MenuItem value="10">10 Minutes</MenuItem>
                                        <MenuItem value="15">15 Minutes</MenuItem>
                                        <MenuItem value="30">30 Minutes</MenuItem>
                                        <MenuItem value="60">60 Minutes</MenuItem>
                                    </Select>
                                </Box>
                                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                    <SettingLabel label="PTZ Schedule Mode" />
                                    <Select
                                        size="small"
                                        value={schedulePtzMode}
                                        onChange={(e) => {
                                            const val = e.target.value;
                                            setSchedulePtzMode(val);
                                            handleSaveSchedule(enableWidelapse, scheduleInterval, val);
                                        }}
                                        className="schedule-inline-select"
                                    >
                                        <MenuItem value="current">Current Angle</MenuItem>
                                        <MenuItem value="tour">Patrol Tour</MenuItem>
                                        <MenuItem value="home">Home</MenuItem>
                                    </Select>
                                </Box>

                                <Tooltip title="Immediately trigger a high-resolution snapshot and save it" arrow placement="top">
                                    <Button
                                        variant="contained"
                                        fullWidth
                                        size="small"
                                        onClick={() => handleDownloadSnapshot(camera?.thumbnail || camera?.image_url, `Widelapse_Capture_${selectedDate}.jpg`)}
                                        startIcon={<FaCamera size={12} />}
                                        sx={{
                                            mt: 1,
                                            background: "#ffcc33",
                                            color: "#000",
                                            fontWeight: 700,
                                            textTransform: "none",
                                            "&:hover": { background: "#ffd65c" }
                                        }}
                                    >
                                        Trigger Instant Capture
                                    </Button>
                                </Tooltip>
                            </Box>
                        </Box>
                    </Box>
                </Box>
            )}

            {/* =========================================================================
                TAB 4: SETTINGS VIEW
               ========================================================================= */}
            {activeTopTab === "settings" && (
                <Box className="ptz-settings-container">
                    {/* Card 1: Camera & Stream Configuration */}
                    <Box className="ptz-settings-card">
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                            <Typography className="ptz-settings-card-title">
                                <FaGear color="#ffcc33" /> Camera & Stream Configuration
                            </Typography>
                            <CardInfoTooltip
                                title="Camera & Stream Configuration"
                                purpose="Defines core hardware identity, network connectivity, and live video stream resolution parameters."
                                details={[
                                    { label: "Camera Name", desc: "Descriptive station name across system dashboards, live viewers, and reports." },
                                    { label: "IP Address", desc: "Network IP gateway assigned to communicate with the camera." },
                                    { label: "Camera Model", desc: "Hardware vendor profile and optical sensor architecture." },
                                    { label: "Resolution", desc: "Streaming and image capture resolution (4K UHD, Full HD 1080p, or HD 720p)." },
                                    { label: "Frame Rate", desc: "Video playback smoothness in frames per second (25 FPS standard)." }
                                ]}
                            />
                        </Box>
                        <Box className="ptz-settings-form-row">
                            <SettingLabel label="Camera Name" />
                            <input
                                className="ptz-settings-input"
                                value={settingCamName}
                                onChange={(e) => setSettingCamName(e.target.value)}
                            />
                        </Box>
                        <Box className="ptz-settings-form-row">
                            <SettingLabel label="IP Address" />
                            <input
                                className="ptz-settings-input"
                                value={settingIpAddress}
                                onChange={(e) => setSettingIpAddress(e.target.value)}
                            />
                        </Box>
                        <Box className="ptz-settings-form-row">
                            <SettingLabel label="Camera Model" />
                            <span style={{ fontSize: 12, fontWeight: 600 }}>WIDELAPSE CAM</span>
                        </Box>
                        <Box className="ptz-settings-form-row">
                            <SettingLabel label="Resolution" />
                            <Select
                                size="small"
                                value={settingResolution}
                                onChange={(e) => setSettingResolution(e.target.value)}
                                className="schedule-inline-select"
                            >
                                <MenuItem value="3840 x 2160 (4K)">3840 x 2160 (4K UHD)</MenuItem>
                                <MenuItem value="1920 x 1080 (FHD)">1920 x 1080 (Full HD)</MenuItem>
                                <MenuItem value="1280 x 720 (HD)">1280 x 720 (HD)</MenuItem>
                            </Select>
                        </Box>
                        <Box className="ptz-settings-form-row">
                            <SettingLabel label="Frame Rate" />
                            <span style={{ fontSize: 12, fontWeight: 600 }}>25 FPS</span>
                        </Box>
                    </Box>

                    {/* Card 2: Widelapse Schedule & Automation */}
                    <Box className="ptz-settings-card">
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                            <Typography className="ptz-settings-card-title">
                                <FaSliders color="#ffcc33" /> Widelapse Schedule & Intervals
                            </Typography>
                            <CardInfoTooltip
                                title="Widelapse Schedule & Automation"
                                purpose="Configures background recurring timelapse shooting schedules, interval frequencies, and working hours."
                                details={[
                                    { label: "Enable Schedule", desc: "Master toggle to activate or suspend automated periodic photo captures." },
                                    { label: "Upload Interval", desc: "Capture cycle frequency (every 10, 15, 30, or 60 minutes)." },
                                    { label: "Working Hours Window", desc: "Daily active timeframe (e.g., 06:00 AM - 06:00 PM). Captures pause outside this window." },
                                    { label: "PTZ Schedule Action", desc: "Movement behavior before shooting: Current Angle, Patrol Tour, or Return to Home." }
                                ]}
                            />
                        </Box>
                        <Box className="ptz-settings-form-row">
                            <SettingLabel label="Enable Widelapse Schedule" />
                            <Switch
                                size="small"
                                checked={enableWidelapse}
                                onChange={(e) => setEnableWidelapse(e.target.checked)}
                            />
                        </Box>
                        <Box className="ptz-settings-form-row">
                            <SettingLabel label="Upload Interval" />
                            <Select
                                size="small"
                                value={scheduleInterval}
                                onChange={(e) => setScheduleInterval(e.target.value)}
                                className="schedule-inline-select"
                            >
                                <MenuItem value="10">10 Minutes</MenuItem>
                                <MenuItem value="15">15 Minutes</MenuItem>
                                <MenuItem value="30">30 Minutes</MenuItem>
                                <MenuItem value="60">60 Minutes</MenuItem>
                            </Select>
                        </Box>
                        <Box className="ptz-settings-form-row">
                            <SettingLabel label="Working Hours Window" />
                            <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                                <Select size="small" value={startTime} onChange={(e) => setStartTime(e.target.value)} className="schedule-inline-select">
                                    <MenuItem value="06:00">06:00 AM</MenuItem>
                                    <MenuItem value="07:00">07:00 AM</MenuItem>
                                    <MenuItem value="08:00">08:00 AM</MenuItem>
                                </Select>
                                <span style={{ fontSize: 11, color: "#888" }}>to</span>
                                <Select size="small" value={endTime} onChange={(e) => setEndTime(e.target.value)} className="schedule-inline-select">
                                    <MenuItem value="17:00">05:00 PM</MenuItem>
                                    <MenuItem value="18:00">06:00 PM</MenuItem>
                                    <MenuItem value="19:00">07:00 PM</MenuItem>
                                </Select>
                            </Box>
                        </Box>
                        <Box className="ptz-settings-form-row">
                            <SettingLabel label="PTZ Schedule Action" />
                            <Select
                                size="small"
                                value={schedulePtzMode}
                                onChange={(e) => setSchedulePtzMode(e.target.value)}
                                className="schedule-inline-select"
                            >
                                <MenuItem value="current">Current Angle</MenuItem>
                                <MenuItem value="tour">Patrol Tour (All Presets)</MenuItem>
                                <MenuItem value="home">Return to Home</MenuItem>
                            </Select>
                        </Box>
                    </Box>

                    {/* Card 3: PTZ Hardware & Motion Controls */}
                    <Box className="ptz-settings-card">
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                            <Typography className="ptz-settings-card-title">
                                <FaCrosshairs color="#ffcc33" /> PTZ Motion & Presets
                            </Typography>
                            <CardInfoTooltip
                                title="PTZ Motion & Preset Mechanics"
                                purpose="Controls pan/tilt motor dynamics, tour transition dwell times, and auto-idle return behavior."
                                details={[
                                    { label: "Default PTZ Speed", desc: "Motor rotation speed (Slow, Medium, Fast) for directional adjustments." },
                                    { label: "Tour Dwell Time", desc: "Pause interval in seconds at each preset before moving to the next during patrol tours." },
                                    { label: "Auto-Return Home", desc: "Automatically returns camera to default Home position after 5 minutes of idle inactivity." }
                                ]}
                            />
                        </Box>
                        <Box className="ptz-settings-form-row">
                            <SettingLabel label="Default PTZ Speed" />
                            <Box className="speed-toggle-group">
                                <button className={`speed-pill ${speed === "slow" ? "active" : ""}`} onClick={() => setSpeed("slow")}>Slow</button>
                                <button className={`speed-pill ${speed === "medium" ? "active" : ""}`} onClick={() => setSpeed("medium")}>Med</button>
                                <button className={`speed-pill ${speed === "fast" ? "active" : ""}`} onClick={() => setSpeed("fast")}>Fast</button>
                            </Box>
                        </Box>
                        <Box className="ptz-settings-form-row">
                            <SettingLabel label="Preset Tour Dwell Time" />
                            <Select
                                size="small"
                                value={settingDwellTime}
                                onChange={(e) => setSettingDwellTime(e.target.value)}
                                className="schedule-inline-select"
                            >
                                <MenuItem value={5}>5 Seconds</MenuItem>
                                <MenuItem value={6}>6 Seconds</MenuItem>
                                <MenuItem value={10}>10 Seconds</MenuItem>
                                <MenuItem value={15}>15 Seconds</MenuItem>
                            </Select>
                        </Box>
                        <Box className="ptz-settings-form-row">
                            <SettingLabel label="Auto-Return Home on Idle" />
                            <Switch
                                size="small"
                                checked={settingAutoHome}
                                onChange={(e) => setSettingAutoHome(e.target.checked)}
                            />
                        </Box>
                    </Box>

                    {/* Card 4: Storage & Maintenance */}
                    <Box className="ptz-settings-card">
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                            <Typography className="ptz-settings-card-title">
                                <FaCamera color="#ffcc33" /> Storage & Retention
                            </Typography>
                            <CardInfoTooltip
                                title="Storage & Retention Management"
                                purpose="Monitors edge and cloud storage consumption, data retention policies, and firmware health."
                                details={[
                                    { label: "Retention Policy", desc: "Archive storage lifespan (30, 60, 90 days, or 1 year) before automated compression." },
                                    { label: "SD Card Storage", desc: "On-camera edge storage buffer status used for offline resilience." },
                                    { label: "Cloud Sync Storage", desc: "Server capacity utilized by high-res image archives and timelapse reels." },
                                    { label: "Firmware Version", desc: "Detected camera operating system version and security patch status." }
                                ]}
                            />
                        </Box>
                        <Box className="ptz-settings-form-row">
                            <SettingLabel label="Image Retention Policy" />
                            <Select
                                size="small"
                                value={settingRetention}
                                onChange={(e) => setSettingRetention(e.target.value)}
                                className="schedule-inline-select"
                            >
                                <MenuItem value={30}>30 Days</MenuItem>
                                <MenuItem value={60}>60 Days</MenuItem>
                                <MenuItem value={90}>90 Days</MenuItem>
                                <MenuItem value={365}>1 Year</MenuItem>
                            </Select>
                        </Box>
                        <Box className="ptz-settings-form-row">
                            <SettingLabel label="SD Card Storage" />
                            <span style={{ fontSize: 12, fontWeight: 600 }}>182 GB / 512 GB (36%)</span>
                        </Box>
                        <Box className="ptz-settings-form-row">
                            <SettingLabel label="Cloud Sync Storage" />
                            <span style={{ fontSize: 12, fontWeight: 600 }}>1.2 TB / 5 TB (24%)</span>
                        </Box>
                        <Box className="ptz-settings-form-row">
                            <SettingLabel label="Firmware Version" />
                            <span style={{ fontSize: 12, color: "#22c55e", fontWeight: 600 }}>WIDELAPSE OS 2.4.1 (Latest)</span>
                        </Box>
                    </Box>

                    {/* Footer Save Button */}
                    <Box className="ptz-settings-footer-actions">
                        <Tooltip title="Save and apply all camera configuration, timelapse schedule, and motion settings to the database" arrow placement="top">
                            <Button
                                variant="contained"
                                size="medium"
                                disabled={scheduleSaving}
                                onClick={handleSaveAllSettings}
                                startIcon={scheduleSaving ? <CircularProgress size={14} sx={{ color: "#000" }} /> : <FaCheck size={14} />}
                                sx={{
                                    background: "#ffcc33",
                                    color: "#000",
                                    fontWeight: 700,
                                    fontSize: "13px",
                                    px: 3,
                                    py: 1,
                                    textTransform: "none",
                                    borderRadius: "8px",
                                    boxShadow: "0 4px 12px rgba(255,204,51,0.3)",
                                    "&:hover": { background: "#ffd65c" }
                                }}
                            >
                                {scheduleSaving ? "Saving..." : "Save Camera Settings"}
                            </Button>
                        </Tooltip>
                    </Box>
                </Box>
            )}
            {/* Modal: Set Current Position as Preset */}
            <Dialog
                open={isAddPresetOpen}
                onClose={() => setIsAddPresetOpen(false)}
                PaperProps={{
                    sx: {
                        background: "#1c1c1c",
                        color: "#fff",
                        border: "1px solid rgba(255,204,51,0.4)",
                        borderRadius: "12px",
                        p: 1
                    }
                }}
            >
                <DialogTitle sx={{ color: "#ffcc33", fontWeight: 700, fontSize: 16 }}>
                    Set Current Position as Preset
                </DialogTitle>
                <DialogContent>
                    <Typography sx={{ color: "#aaa", fontSize: 12, mb: 1.5 }}>
                        Enter a unique name for this camera position.
                    </Typography>
                    <TextField
                        autoFocus
                        fullWidth
                        size="small"
                        placeholder="e.g. TOWER_CRANE_TOP"
                        value={newPresetName}
                        onChange={(e) => setNewPresetName(e.target.value)}
                        sx={{
                            "& .MuiOutlinedInput-root": {
                                color: "#fff",
                                backgroundColor: "rgba(255,255,255,0.05)",
                                "& fieldset": { borderColor: "rgba(255,255,255,0.2)" },
                                "&:hover fieldset": { borderColor: "#ffcc33" },
                                "&.Mui-focused fieldset": { borderColor: "#ffcc33" }
                            }
                        }}
                    />
                </DialogContent>
                <DialogActions sx={{ pb: 1.5, pr: 2 }}>
                    <Button
                        onClick={() => setIsAddPresetOpen(false)}
                        sx={{ color: "#888", textTransform: "none" }}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleSaveNewPreset}
                        sx={{
                            background: "#ffcc33",
                            color: "#000",
                            fontWeight: 700,
                            textTransform: "none",
                            "&:hover": { background: "#ffd65c" }
                        }}
                    >
                        Save Preset
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
